import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { IIntegrationDTO, INotificationDTO, ISchedulingResource } from '@shared/common';

/** Result of starting an OAuth connect: the provider authorize URL to redirect the tenant to. */
export interface IConnectStartResult {
  authorizeURL: string;
}

/** A tenant's integrations together with the id of the single calendar bookings are written to (null when none is connected). */
export interface IIntegrationsResult {
  integrations: IIntegrationDTO[];
  activeSchedulingIntegrationID: string | null;
}

/**
 * Client for a tenant's connected integrations and outbound notifications.
 *
 * Tenant-facing reads route through the lobby service dashboard surface (`dashboard/:tenantID/...`).
 */
export class IntegrationClient {
  /** Singleton instance. */
  private static instance: IntegrationClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton IntegrationClient instance. */
  public static GetInstance(): IntegrationClient {
    return IntegrationClient.instance ?? (IntegrationClient.instance = new IntegrationClient());
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/integrations - List a tenant's connected integrations and the active scheduling pointer.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The tenant's integrations and the id of the calendar bookings are written to.
   */
  public async ListIntegrations(signal: AbortSignal, tenantID: string): Promise<IIntegrationsResult> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/integrations`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch integrations');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/notifications - List a tenant's outbound notifications, newest first, with optional free-text search and pagination.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (recipient/channel/status/subject/body).
   * @returns The tenant's notifications page, most recent first.
   */
  public async ListNotifications(
    signal: AbortSignal,
    tenantID: string,
    offset: number = CommonConstants.Pagination.Offset,
    limit: number = CommonConstants.Pagination.Limit,
    pattern?: string
  ): Promise<INotificationDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/notifications?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch notifications');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/notifications/count - Count a tenant's notifications matching an optional free-text search term.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param pattern - Optional free-text search term.
   * @returns The number of matching notifications.
   */
  public async CountNotifications(signal: AbortSignal, tenantID: string, pattern?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/notifications/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch notification count');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/integrations/scheduling/:provider/connect - Begin an OAuth connect for a scheduling provider.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param provider - The scheduling provider slug.
   * @returns The provider authorize URL to redirect the tenant to.
   */
  public async ConnectScheduling(signal: AbortSignal, tenantID: string, provider: string): Promise<IConnectStartResult> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/integrations/scheduling/${provider}/connect`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to start scheduling connect');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/integrations/:integrationID/resources - List the connected account's selectable resources.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param integrationID - The connected integration id.
   * @returns The selectable resources (calendars / event types / services).
   */
  public async ListResources(signal: AbortSignal, tenantID: string, integrationID: string): Promise<ISchedulingResource[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/integrations/${integrationID}/resources`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch integration resources');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/integrations/:integrationID/select - Persist the chosen resource and activate the integration.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param integrationID - The connected integration id.
   * @param targetID - The chosen resource id.
   * @returns The activated integration.
   */
  public async SelectResource(signal: AbortSignal, tenantID: string, integrationID: string, targetID: string): Promise<IIntegrationDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `dashboard/${tenantID}/integrations/${integrationID}/select`,
      { method: FrontendConstants.MethodPost, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ targetID }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to select integration resource');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/integrations/:integrationID/activate - Make a connected scheduling integration the tenant's single active booking calendar. Any previously active calendar
   * stays connected; only the active pointer moves, so no appointments are transferred.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param integrationID - The integration id to make active.
   * @returns The now-active integration.
   */
  public async SetActiveScheduling(signal: AbortSignal, tenantID: string, integrationID: string): Promise<IIntegrationDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/integrations/${integrationID}/activate`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to set active calendar');
    return await res.json();
  }

  /**
   * DELETE /api/lobby/dashboard/:tenantID/integrations/:integrationID - Disconnect a scheduling integration.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param integrationID - The integration id to disconnect.
   * @returns The deactivated integration.
   */
  public async Disconnect(signal: AbortSignal, tenantID: string, integrationID: string): Promise<IIntegrationDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/integrations/${integrationID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to disconnect integration');
    return await res.json();
  }
}
